// Editor de correo con formato — el mismo en todos los sitios donde se
// escribe un correo que va a salir del CRM.
//
// Lissette 2026-08-20, dos veces: en Secuencias y en Plantillas personalizadas
// quiere "el mismo editor que cuando enviamos un email", con imágenes y
// enlaces. Hasta ahora Secuencias era una caja de HTML crudo (había que
// escribir `<p>` a mano) y las Plantillas un textarea pelado.
//
// Está calcado del editor que ya funciona en el correo de la ficha del cliente
// (ClientCommsTab) y le añade lo que allí falta: insertar ENLACE y un botón de
// IMAGEN, además del pegar/arrastrar que ya existía.
//
// Notas de por qué está hecho así:
//
//   • El valor es HTML, porque es lo que se manda. El cuadro se sincroniza
//     SOLO cuando el HTML de fuera no es el que este editor acaba de emitir:
//     reescribir `innerHTML` en cada tecleo le mueve el cursor al principio a
//     la persona que está escribiendo. Mismo truco que RichNoteBox.
//   • Los `<select>` de letra y tamaño roban el foco y con él la selección, así
//     que se guarda el rango al pulsar y se restaura antes de aplicar. Los
//     botones no lo necesitan: usan onMouseDown + preventDefault.
//   • Las variables ({{firstName}}…) entran por el cursor. useVarInserter sabe
//     hacerlo con selectionStart, que un contentEditable no tiene, así que este
//     editor le pasa su propia función de insertar (tercer argumento de bind).
import { quitarColoresInvisibles } from '../coloresDelCorreo.js';
import { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, Link2, Image as ImageIcon,
} from 'lucide-react';
import { toast } from './Toast.jsx';
import EmojiPicker from './EmojiPicker.jsx';
import { limpiarHtmlPegado } from '../sanitizeHtml.js';

const FUENTES = ['Arial', 'Georgia', 'Times New Roman', 'Courier New', 'Verdana', 'Tahoma'];

// Saca un archivo de imagen de un pegado o de un arrastre. Los navegadores lo
// exponen en .items O en .files, según de dónde venga.
function imagenDe(dt) {
  if (!dt) return null;
  for (const it of (dt.items || [])) {
    if (it.kind === 'file' && it.type && it.type.startsWith('image/')) {
      const f = it.getAsFile(); if (f) return f;
    }
  }
  for (const f of (dt.files || [])) {
    if ((f.type && f.type.startsWith('image/')) || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(f.name || '')) return f;
  }
  return null;
}

export default function EmailRichEditor({
  value,
  onChange,
  placeholder,
  lang = 'es',
  inserter,          // useVarInserter() del componente de arriba (opcional)
  // Mando a distancia: quien monta este editor puede meter texto o HTML en el
  // cursor desde SUS propios botones (el PDF y el enlace de Email Masivo viven
  // fuera de esta barra). Sin esto, cada pantalla que lo usara tendria que
  // volver a resolver "meterlo donde esta el cursor", que es justo la parte
  // dificil de un contentEditable.
  apiRef,
  // Aviso de foco hacia arriba, para pantallas que necesitan saber en que
  // campo se esta escribiendo.
  onFoco,
  minHeight = 150,
  style,
  disabled = false,
  // BOTONES QUE SE PUEDEN QUITAR, para quien no puede honrarlos.
  //
  // El editor nacio para correos, donde todo lo de la barra sale igual en el
  // buzon del cliente. Desde el 2026-09-16 lo usa tambien el editor de
  // plantillas de FIRMAS, y ahi el documento acaba dibujado en un PDF con
  // pdf-lib: la negrita, la cursiva, el subrayado y la alineacion si se
  // dibujan; una imagen, un enlace o un tamano de letra por trozo, no. Ofrecer
  // un boton cuyo efecto desaparece en el documento firmado es peor que no
  // tenerlo — la agente lo usa, lo ve en la pantalla, y el PDF sale sin eso.
  sinImagen = false,
  sinEnlace = false,
  sinFuente = false,
  // ESCRIBIR SOBRE PAPEL BLANCO (Lissette 2026-09-23, con captura del editor
  // de plantillas de Firmas: "en modo oscuro no se ve nada").
  //
  // Un documento trae su propio color de texto —negro, sobre todo si se pegó
  // de Word o salió de un PDF— y este cuadro pintaba sobre el fondo oscuro del
  // CRM: negro sobre casi negro. En tema claro se leía por pura suerte. Con
  // `hoja` el cuadro es una hoja blanca con letra oscura, que además es como lo
  // verá el cliente (la misma idea que HojaDelDocumento, E-317).
  hoja = false,
}) {
  const es = lang === 'es';
  const ref = useRef(null);
  const fileRef = useRef(null);
  const ultimoEmitido = useRef(null);
  const rangoGuardado = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [enfocado, setEnfocado] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const entra = String(value ?? '');
    if (entra === ultimoEmitido.current) return; // es nuestro propio eco
    el.innerHTML = entra;
    ultimoEmitido.current = entra;
  }, [value]);

  const emitir = () => {
    const el = ref.current;
    if (!el) return;
    // Sin el color de letra del tema oscuro: el navegador lo copia dentro del
    // texto al cambiar la fuente, y en el correo (fondo blanco) no se ve (E-397).
    // Solo se limpia lo que se GUARDA; la caja sigue mostrando lo que hay.
    const siguiente = quitarColoresInvisibles(el.innerHTML);
    ultimoEmitido.current = siguiente;
    onChange(siguiente);
  };

  const guardarRango = () => {
    try {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
        rangoGuardado.current = sel.getRangeAt(0).cloneRange();
      }
    } catch { /* sin selección utilizable: se aplicará al final */ }
  };

  const restaurarRango = () => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const r = rangoGuardado.current;
    if (!r) return;
    try {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
    } catch { /* noop */ }
  };

  const aplicar = (cmd, val) => {
    restaurarRango();
    try { document.execCommand('styleWithCSS', false, true); } catch { /* no en todos */ }
    document.execCommand(cmd, false, val);
    emitir();
  };

  const insertarHtml = (html) => {
    restaurarRango();
    document.execCommand('insertHTML', false, html);
    emitir();
  };

  // Las variables van como TEXTO: {{firstName}} tiene que llegar tal cual al
  // motor de envío, no convertido en marcado.
  const insertarTexto = (txt) => {
    restaurarRango();
    if (!document.execCommand('insertText', false, txt)) insertarHtml(txt);
    else emitir();
  };

  const ponerEnlace = () => {
    guardarRango();
    const url = window.prompt(es ? 'Dirección del enlace (https://…)' : 'Link address (https://…)', 'https://');
    if (!url || url === 'https://') return;
    const limpia = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const sel = window.getSelection();
    const haySeleccion = sel && String(sel).trim().length > 0 && rangoGuardado.current && !rangoGuardado.current.collapsed;
    if (haySeleccion) {
      aplicar('createLink', limpia);
    } else {
      // Sin nada seleccionado no hay qué enlazar: se pregunta el texto, porque
      // pegar la URL cruda en un correo se ve como un correo de spam.
      const texto = window.prompt(es ? 'Texto que se verá' : 'Text to show', limpia) || limpia;
      const e = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      insertarHtml(`<a href="${e(limpia)}" target="_blank" rel="noopener noreferrer">${e(texto)}</a>`);
    }
  };

  const subirImagen = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const { uploadEmailImage } = await import('../api.js');
      const url = await uploadEmailImage(file);
      insertarHtml(`<img src="${url}" alt="" style="max-width:100%;height:auto;border-radius:8px;" />`);
      toast.success(es ? 'Imagen agregada ✓' : 'Image added ✓');
    } catch (e) {
      toast.error(e.message || (es ? 'No se pudo subir la imagen' : 'Could not upload image'));
    } finally { setSubiendo(false); }
  };

  /**
   * PEGAR: la imagen se sube, y el HTML se limpia ANTES de entrar.
   *
   * Crismar Perez, 2026-09-17: una carta pegada desde Word no se podia enviar
   * ("String must contain at most 20000 characters"). El navegador, si se le
   * deja, pega el HTML de origen ENTERO — `mso-`, `<o:p>`, un `<span>` por
   * palabra— y una carta de una pagina se vuelve cientos de kilobytes. El texto
   * es el mismo; lo que pesa es la basura, y ademas nada de ella se dibuja en el
   * PDF firmado, asi que la pantalla y el documento firmado enseñarian cosas
   * distintas.
   *
   * Se limpia con las MISMAS banderas de este editor: entra lo que sus botones
   * pueden hacer. Ver `htmlPegado.js`.
   *
   * Si el portapapeles no trae `text/html` —texto plano, otra app— no se toca
   * nada: lo pega el navegador como siempre, y ya viene limpio.
   */
  const pegar = (e) => {
    const f = imagenDe(e.clipboardData);
    if (f) { e.preventDefault(); guardarRango(); subirImagen(f); return; }
    const html = e.clipboardData && e.clipboardData.getData('text/html');
    if (!html) return;
    e.preventDefault();
    // El cursor esta donde la persona pego; se guarda AHORA y no se confia en
    // el ultimo rango, que puede ser de antes de mover el raton.
    guardarRango();
    const limpio = limpiarHtmlPegado(html, { sinImagen, sinEnlace, sinFuente });
    // Si del recorte no queda nada con lo que escribir (una tabla, una imagen
    // donde no se admiten), se pega el texto: perder el pegado entero seria
    // peor que perder su formato.
    if (limpio.replace(/<[^>]*>/g, '').trim()) insertarHtml(limpio);
    else insertarTexto((e.clipboardData && e.clipboardData.getData('text/plain')) || '');
  };

  const bound = inserter ? inserter.bind(() => String(value ?? ''), onChange, insertarTexto) : null;

  // El mando a distancia se rellena en cada render: las funciones de arriba se
  // vuelven a crear con cada uno, y guardar las de la primera vez dejaria el
  // boton del PDF escribiendo sobre un estado viejo.
  if (apiRef) apiRef.current = { insertarTexto, insertarHtml, guardarRango };

  const btn = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 6,
    border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
    color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer', padding: 0,
  };
  const sep = { width: 1, height: 18, background: 'var(--c-border,#1a3550)', margin: '0 2px' };
  const selSt = {
    height: 26, borderRadius: 6, border: '1px solid var(--c-border,#1a3550)',
    background: 'var(--c-surface2,#112538)', color: 'var(--c-text2,#7aa3c0)',
    fontSize: '.68rem', padding: '0 .25rem', cursor: 'pointer', fontFamily: 'inherit',
  };
  const herramienta = (titulo, cmd, Icono) => (
    <button type="button" title={titulo} disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); guardarRango(); }}
      onClick={() => aplicar(cmd)} style={btn}><Icono size={13} /></button>
  );

  const vacio = !String(value ?? '').replace(/<[^>]*>/g, '').trim() && !/<img/i.test(String(value ?? ''));

  return (
    <div style={style}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
        {herramienta(es ? 'Negrita' : 'Bold', 'bold', Bold)}
        {herramienta(es ? 'Cursiva' : 'Italic', 'italic', Italic)}
        {herramienta(es ? 'Subrayado' : 'Underline', 'underline', Underline)}
        <span style={sep} />
        {herramienta(es ? 'Izquierda' : 'Left', 'justifyLeft', AlignLeft)}
        {herramienta(es ? 'Centrar' : 'Center', 'justifyCenter', AlignCenter)}
        {herramienta(es ? 'Derecha' : 'Right', 'justifyRight', AlignRight)}
        {/* Justificado (Lissette 2026-09-02, queja 9: "colocar en todos los
            lugares donde se redacte correo o plantilla la opcion de edicion
            justificada"). Estaban izquierda, centro y derecha; faltaba esta,
            en los TRES editores del CRM. */}
        {herramienta(es ? 'Justificar' : 'Justify', 'justifyFull', AlignJustify)}
        <span style={sep} />
        {/* Emoticonos (Lissette 2026-09-03). `alPulsar` guarda la seleccion
            antes de que el foco se vaya al panel: sin eso el emoji cae al final
            del texto en vez de donde estaba el cursor. */}
        <EmojiPicker es={es} disabled={disabled} alPulsar={guardarRango}
          onElegir={(emoji) => insertarTexto(emoji)} estiloBoton={btn} />
        {!sinEnlace && (
          <button type="button" title={es ? 'Insertar enlace' : 'Insert link'} disabled={disabled}
            onMouseDown={(e) => { e.preventDefault(); guardarRango(); }}
            onClick={ponerEnlace} style={btn}><Link2 size={13} /></button>
        )}
        {!sinImagen && (
          <button type="button" title={es ? 'Insertar imagen' : 'Insert image'} disabled={disabled || subiendo}
            onMouseDown={(e) => { e.preventDefault(); guardarRango(); }}
            onClick={() => fileRef.current && fileRef.current.click()} style={btn}>
            {subiendo ? <span style={{ fontSize: 11 }}>⏳</span> : <ImageIcon size={13} />}
          </button>
        )}
        {/* El input se vacía ANTES de usar el archivo: limpiar `value` después
            deja la FileList vacía y no se sube nada (mismo fallo que tuvo la
            camarita del SMS). */}
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) subirImagen(f); }} />
        {!sinFuente && <span style={sep} />}
        {!sinFuente && <select title={es ? 'Tipo de letra' : 'Font'} defaultValue="" disabled={disabled}
          onMouseDown={guardarRango}
          onChange={(e) => { const v = e.target.value; if (v) aplicar('fontName', v); e.target.value = ''; }}
          style={selSt}>
          <option value="">{es ? 'Letra' : 'Font'}</option>
          {FUENTES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>}
        {!sinFuente && <select title={es ? 'Tamaño' : 'Size'} defaultValue="" disabled={disabled}
          onMouseDown={guardarRango}
          onChange={(e) => { const v = e.target.value; if (v) aplicar('fontSize', v); e.target.value = ''; }}
          style={selSt}>
          <option value="">{es ? 'Tamaño' : 'Size'}</option>
          <option value="2">{es ? 'Pequeño' : 'Small'}</option>
          <option value="3">{es ? 'Normal' : 'Normal'}</option>
          <option value="5">{es ? 'Grande' : 'Large'}</option>
          <option value="6">{es ? 'Muy grande' : 'Huge'}</option>
        </select>}
      </div>

      <div style={{ position: 'relative' }}>
        <div
          ref={ref}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder || (es ? 'Mensaje' : 'Message')}
          onInput={emitir}
          onKeyUp={guardarRango}
          onMouseUp={guardarRango}
          onFocus={(e) => { setEnfocado(true); if (bound) bound.onFocus(e); if (onFoco) onFoco(); }}
          onBlur={() => { setEnfocado(false); guardarRango(); emitir(); }}
          onPaste={(e) => pegar(e)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { const f = imagenDe(e.dataTransfer); if (f) { e.preventDefault(); guardarRango(); subirImagen(f); } }}
          style={{
            width: '100%', minHeight, boxSizing: 'border-box',
            background: hoja ? '#ffffff' : 'var(--c-surface2,#112538)',
            border: `1px solid ${enfocado ? 'rgba(0,212,255,.45)' : 'var(--c-border,#1a3550)'}`,
            borderRadius: 8, padding: '.6rem .7rem',
            color: hoja ? '#1a1a1a' : 'var(--c-white,#eef6ff)', fontSize: '.84rem', lineHeight: 1.55,
            outline: 'none', overflowY: 'auto', textAlign: 'left',
          }}
        />
        {vacio && placeholder && (
          <div style={{
            position: 'absolute', top: '.6rem', left: '.7rem', pointerEvents: 'none',
            color: hoja ? '#94a3b8' : 'var(--c-text3,#45688a)', fontSize: '.84rem',
          }}>{placeholder}</div>
        )}
      </div>
      <div style={{ fontSize: '.64rem', color: 'var(--c-text3,#45688a)', marginTop: 3 }}>
        {es
          ? 'Puedes pegar o arrastrar una imagen directamente en el cuadro.'
          : 'You can paste or drag an image straight into the box.'}
      </div>
    </div>
  );
}
